import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { Paper } from '../../components/Paper';
import { ErrorNotice } from '../../components/States';
import { useToast } from '../../components/Toast';
import { deleteMyAccount } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import safety from '../posts/Safety.module.css';
import styles from '../../routes/Me.module.css';

/** Permanent account deletion, with a plain-language list of what goes. */
export function DeleteAccount() {
  const [open, setOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const remove = useMutation({
    mutationFn: deleteMyAccount,
    onSuccess: async () => {
      // The server already ended the session; clear it from this browser too.
      await supabase.auth.signOut({ scope: 'local' });
      toast.show('Your account has been deleted.');
      navigate('/welcome', { replace: true });
    },
  });

  const close = () => {
    setOpen(false);
    setUnderstood(false);
    remove.reset();
  };

  return (
    <Paper as="section" className={styles.panel} aria-labelledby="delete-account">
      <h2 id="delete-account" className={styles.panelTitle}>
        Delete account
      </h2>
      <div className={styles.row}>
        <span className={styles.muted}>Permanently remove your account and everything you’ve posted.</span>
        <Button size="small" variant="danger" onClick={() => setOpen(true)}>
          Delete my account
        </Button>
      </div>

      <Dialog open={open} onClose={close} title="Delete your account?">
        <p>This permanently deletes:</p>
        <ul className={styles.privacy}>
          <li>your name, bio and email address</li>
          <li>your home area and location checks</li>
          <li>your notes and photos, including neighbors’ replies on your notes</li>
          <li>your replies, thanks, blocks and reports</li>
        </ul>
        <p className={safety.fine}>
          This can’t be undone. You can join again later with the same email, but you’ll start fresh.
        </p>
        <label className={safety.reason}>
          <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />I understand
          this can’t be undone
        </label>
        {remove.error && <ErrorNotice error={remove.error} />}
        <div className={safety.actions}>
          <Button variant="quiet" onClick={close}>
            Keep my account
          </Button>
          <Button variant="danger" onClick={() => remove.mutate()} disabled={!understood || remove.isPending}>
            {remove.isPending ? 'Deleting…' : 'Delete my account'}
          </Button>
        </div>
      </Dialog>
    </Paper>
  );
}
